import { useEffect, useState } from "react";
import api from "../../lib/api";
import { Gift, Loader2, RefreshCw } from "lucide-react";

/**
 * Manque à gagner promo bienvenue — Admin KPI.
 * Displays 3 sub-counters (today / this week / this month) showing the sum
 * of TFT commissions the platform absorbed because of the WELCOME15 promo.
 */
export default function PromoLostCommissionKPI() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchData = async () => {
    try {
      setLoading(true);
      setError(null);
      const { data: res } = await api.get("/admin/stats/promo-lost-commission");
      setData(res);
    } catch (e) {
      setError(e.response?.data?.detail || "Erreur de chargement");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const fmt = (n) => (n || 0).toLocaleString("fr-FR");
  const buckets = [
    { key: "today", label: "Aujourd'hui", accent: "#EF4444" },
    { key: "week", label: "Cette semaine", accent: "#F59E0B" },
    { key: "month", label: "Ce mois", accent: "#0A2540" },
  ];

  return (
    <div
      className="mb-4 rounded-xl border border-[#FACC15]/50 bg-gradient-to-br from-[#FFFBEB] to-white p-4"
      data-testid="promo-lost-commission-kpi"
    >
      <div className="flex items-start justify-between mb-3 gap-3">
        <div className="flex items-start gap-2 min-w-0">
          <div className="mt-0.5 shrink-0 w-8 h-8 rounded-lg bg-[#FACC15]/30 flex items-center justify-center">
            <Gift className="w-4 h-4 text-[#0A2540]" />
          </div>
          <div className="min-w-0">
            <p className="text-sm font-semibold text-[#0A2540]" style={{ fontFamily: "Outfit" }}>
              Manque à gagner promo bienvenue
            </p>
            <p className="text-[11px] text-[#64748B] leading-snug">
              Commissions TFT non prélevées aux chauffeurs — absorbées par la plateforme au titre du bonus{" "}
              <span className="font-mono font-medium text-[#0A2540]">WELCOME15</span>.
            </p>
          </div>
        </div>
        <button
          type="button"
          onClick={fetchData}
          disabled={loading}
          className="h-8 px-2 inline-flex items-center gap-1 text-[11px] rounded-lg border border-[#CBD5E1] text-[#0A2540] hover:bg-white transition disabled:opacity-50"
          data-testid="promo-lost-commission-refresh"
          title="Actualiser"
        >
          {loading ? <Loader2 className="w-3 h-3 animate-spin" /> : <RefreshCw className="w-3 h-3" />}
          Actualiser
        </button>
      </div>

      {error && (
        <div className="text-[11px] text-[#EF4444] py-2" data-testid="promo-lost-commission-error">
          {error}
        </div>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
        {buckets.map((b) => {
          const bucket = data?.[b.key] || { amount: 0, count: 0 };
          return (
            <div
              key={b.key}
              className="rounded-lg bg-white border border-[#E2E8F0] p-3"
              data-testid={`promo-lost-commission-${b.key}`}
            >
              <div className="flex items-center justify-between">
                <span className="text-[11px] font-medium uppercase tracking-wide text-[#64748B]">
                  {b.label}
                </span>
                <span
                  className="text-[10px] px-1.5 py-0.5 rounded-full bg-[#F1F5F9] text-[#64748B]"
                  data-testid={`promo-lost-commission-count-${b.key}`}
                >
                  {bucket.count} course{bucket.count > 1 ? "s" : ""}
                </span>
              </div>
              <div className="mt-1.5 flex items-baseline gap-1">
                <span
                  className="text-2xl font-bold"
                  style={{ color: b.accent, fontFamily: "Outfit" }}
                  data-testid={`promo-lost-commission-amount-${b.key}`}
                >
                  {loading && !data ? "…" : fmt(bucket.amount)}
                </span>
                <span className="text-[11px] font-semibold text-[#64748B]">TFT</span>
              </div>
            </div>
          );
        })}
      </div>

      {data?.all_time && (
        <p className="mt-2 text-[10px] text-[#94A3B8] text-right" data-testid="promo-lost-commission-all-time">
          Cumul depuis le lancement : <strong className="text-[#0A2540]">{fmt(data.all_time.amount)} TFT</strong>{" "}
          sur {data.all_time.count} course{data.all_time.count > 1 ? "s" : ""}.
        </p>
      )}
    </div>
  );
}
